import { type ReactNode, type CSSProperties, type MouseEvent } from 'react';
import { LogIn, LayoutDashboard, CaseUpper, ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';
import AdminSessionToolbar from './AdminSessionToolbar';
import { ReportIssueButton } from './segnalazioni/ReportIssueButton';
import { shellChrome, TL4KHeaderButton, TL4KHeaderLink } from '@tl4k/frontend-shared';
import { isTl4kEmbeddedInShell } from '../utils/tl4kEmbedding';

export type TL4KAppHeaderProps = {
  /** Nome dell'applicazione visualizzato accanto al logo */
  appName: string;
  /** Superficie: calda (Caveat, crema) come mo-mappe; neutra (bianco/sans) come Disegna / svg-edit */
  surface?: 'warm' | 'neutral';
  /** Testo al centro (es. domanda generativa del plugin) */
  subtitle?: string | null;
  /** Badge di stato opzionale (es. PIN sessione, sandbox, solo lettura) */
  statusBadge?: ReactNode;
  /**
   * Strip "senza sessione" + pulsante Entra in sessione (stile standard TL4K).
   */
  standaloneSession?: {
    badgeText: string;
    onEnterSession: () => void;
  };
  /** Contenuto subito dopo nome app / badge (es. PIN insegnante, riga studente) */
  afterTitleSlot?: ReactNode;
  /** Link rapido console admin (icona griglia + etichetta, default "Admin") */
  adminConsole?: {
    href: string;
    label?: string;
    openInNewTab?: boolean;
  };
  /** Contenuto aggiuntivo nel gruppo destro (prima del toggle maiuscole e della toolbar auth) */
  rightContent?: ReactNode;
  /** Contenuto nel gruppo destro (prima di adminConsole) — es. SaveStateIndicator */
  rightGroupContent?: ReactNode;
  /** Stato toggle "tutto maiuscolo" per etichette UI (accessibilità / lettura) */
  labelsUppercaseActive?: boolean;
  /** Se definito, mostra il pulsante AB / maiuscole (come in Disegna) */
  onLabelsUppercaseToggle?: () => void;
  /** Callback invocata dopo login/logout nella toolbar auth */
  onAuthChange?: () => void;
  /** URL di ritorno opzionale per login OAuth admin. */
  loginReturnTo?: string;
  /**
   * Se definito, mostra un pulsante "← backLabel" prima del logo.
   * Usato dal sistema admin-return (useAdminReturnTarget) per tornare all'app chiamante.
   */
  onBackToApp?: () => void;
  /** Etichetta del pulsante back. Default: 'Indietro' */
  backLabel?: string;
  /** Se true, mostra il pulsante fullscreen (solo icona) */
  showFullscreenToggle?: boolean;
  /** Stato fullscreen attivo */
  fullscreenActive?: boolean;
  /** Callback per toggle fullscreen */
  onFullscreenToggle?: () => void;
  /** Contenuto immediatamente a sinistra della toolbar auth (es. schermo intero Techyweb) */
  beforeAuthToolbar?: ReactNode;
  /** Nasconde il pulsante Admin login (es. studente in sessione Techyweb) */
  hideAdminLogin?: boolean;
  /** Se true, arrotonda gli angoli superiori dell'header */
  roundedCorners?: boolean;
  /**
   * Se true, mostra l'header anche in iframe Techyweb (`?tl4k-embedded=1`).
   * Default: false — in shell embedded l'auth è su TL4KAppHeader del parent.
   */
  showWhenEmbedded?: boolean;
};

/**
 * Header standard TL4K — logo, nome app, sessione standalone, centro, console admin,
 * strumenti app (`rightContent`), toggle maiuscole etichette, `AdminSessionToolbar`.
 */
export function TL4KAppHeader({
  appName,
  surface = 'warm',
  subtitle,
  statusBadge,
  standaloneSession,
  afterTitleSlot,
  adminConsole,
  rightContent,
  labelsUppercaseActive = false,
  onLabelsUppercaseToggle,
  onAuthChange,
  loginReturnTo,
  onBackToApp,
  backLabel = 'Indietro',
  showFullscreenToggle = false,
  fullscreenActive = false,
  onFullscreenToggle,
  beforeAuthToolbar,
  hideAdminLogin = false,
  roundedCorners = false,
  rightGroupContent,
  showWhenEmbedded = false,
}: TL4KAppHeaderProps) {
  if (!showWhenEmbedded && isTl4kEmbeddedInShell()) {
    return null;
  }

  const isNeutral = surface === 'neutral';

  const headerStyle: CSSProperties = isNeutral
    ? {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 8,
        padding: '6px 12px',
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        zIndex: 50,
        borderTopLeftRadius: roundedCorners ? 20 : 0,
        borderTopRightRadius: roundedCorners ? 20 : 0,
      }
    : {
        height: shellChrome.height,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 12,
        padding: '0 16px',
        background: '#fffcf7',
        borderBottom: '1px solid #e7e3db',
        zIndex: 30,
        borderTopLeftRadius: roundedCorners ? 20 : 0,
        borderTopRightRadius: roundedCorners ? 20 : 0,
      };

  const titleStyle: CSSProperties = isNeutral
    ? {
        margin: 0,
        fontSize: 'clamp(0.95rem, 2.5vw, 1.25rem)',
        fontWeight: 700,
        flexShrink: 0,
        color: '#0f172a',
      }
    : {
        fontSize: '1.25rem',
        fontWeight: 700,
        flexShrink: 0,
        fontFamily: 'Caveat, cursive',
        color: '#1c1917',
      };

  const logoStyle: CSSProperties = isNeutral
    ? { height: 'clamp(32px, 5vw, 44px)', width: 'auto', objectFit: 'contain' as const, flexShrink: 0 }
    : { height: 32, width: 'auto', objectFit: 'contain' as const, flexShrink: 0 };

  const subtitleBlock =
    subtitle ? (
      <p
        title={subtitle}
        style={{
          flex: 1,
          margin: 0,
          textAlign: 'center',
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: isNeutral ? '0 8px' : '0 16px',
          fontFamily: isNeutral ? 'inherit' : 'Caveat, cursive',
          fontStyle: isNeutral ? 'normal' : 'italic',
          color: '#78716c',
          minWidth: 120,
        }}
      >
        {subtitle}
      </p>
    ) : (
      <div style={{ flex: 1, minWidth: 0 }} />
    );

  const adminLabel = adminConsole?.label ?? 'Admin';
  const handleAdminClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!adminConsole?.openInNewTab) return;
    event.preventDefault();
    event.stopPropagation();
    window.open(adminConsole.href, '_blank', 'noopener,noreferrer');
  };

  return (
    <header style={headerStyle}>
      {onBackToApp && (
        <TL4KHeaderButton variant="neutral" icon={ArrowLeft} onClick={onBackToApp}>
          {backLabel}
        </TL4KHeaderButton>
      )}
      {/* Gruppo sinistro: logo + titolo + rightContent (es. lingua) + badge sessione */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: isNeutral ? 6 : 12,
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <img src="/assets/tl4k_logo.svg" alt="TechLAB4Kids" style={logoStyle} />

        {isNeutral ? (
          <h1 style={titleStyle}>{appName}</h1>
        ) : (
          <span style={titleStyle}>{appName}</span>
        )}

        {/* rightContent subito dopo il titolo (es. LanguageSwitcher) */}
        {rightContent}

        {statusBadge}

        {standaloneSession && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 6,
              marginLeft: isNeutral ? 4 : 0,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: isNeutral ? 'clamp(11px, 1.8vw, 13px)' : 12,
                color: '#1e40af',
                background: '#dbeafe',
                padding: isNeutral ? '4px 10px' : '4px 10px',
                borderRadius: 8,
                maxWidth: '100%',
                fontWeight: isNeutral ? 500 : 600,
                whiteSpace: 'nowrap',
              }}
            >
              {standaloneSession.badgeText}
            </span>
            <TL4KHeaderButton variant="primary" icon={LogIn} onClick={standaloneSession.onEnterSession}>
              Entra in sessione
            </TL4KHeaderButton>
          </div>
        )}

        {afterTitleSlot}
      </div>

      {subtitleBlock}

      {/* Gruppo destro: admin console + fullscreen + uppercase + auth toolbar */}
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: isNeutral ? 6 : 10,
          flexShrink: 0,
        }}
      >
        {rightGroupContent}

        {adminConsole && (
          <TL4KHeaderLink
            variant="neutral"
            icon={LayoutDashboard}
            href={adminConsole.href}
            {...(adminConsole.openInNewTab
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            onClick={handleAdminClick}
            title={adminLabel}
          >
            {adminLabel}
          </TL4KHeaderLink>
        )}

        {/* Pulsante Fullscreen (solo icona) */}
        {showFullscreenToggle && onFullscreenToggle && (
          <TL4KHeaderButton
            variant="neutral"
            icon={fullscreenActive ? Minimize2 : Maximize2}
            aria-label={fullscreenActive ? 'Esci da schermo intero' : 'Schermo intero'}
            title={fullscreenActive ? 'Esci da schermo intero' : 'Schermo intero'}
            onClick={onFullscreenToggle}
          />
        )}

        {/* Pulsante Maiuscolo/Minuscolo (solo icona) */}
        {onLabelsUppercaseToggle && (
          <TL4KHeaderButton
            variant="neutral"
            active={!!labelsUppercaseActive}
            icon={CaseUpper}
            aria-label={
              labelsUppercaseActive
                ? 'Disattiva maiuscolo su etichette e testi'
                : 'Attiva maiuscolo su etichette e testi'
            }
            title={
              labelsUppercaseActive
                ? 'Maiuscolo attivo (etichette e testi)'
                : 'Maiuscolo disattivo — clic per facilitare la lettura'
            }
            onClick={onLabelsUppercaseToggle}
          />
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          {/* Segnalazioni studenti: pulsante discreto, gate interno (nascosto ai teacher) */}
          <ReportIssueButton appName={appName} compact />
          {beforeAuthToolbar}
          <AdminSessionToolbar
            onLoginSuccess={onAuthChange}
            loginReturnTo={loginReturnTo}
            hideAdminLogin={hideAdminLogin}
          />
        </div>
      </div>
    </header>
  );
}
